import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface DashboardStats {
  total_videos: number;
  total_chunks: number;
  videos_processing: number;
  videos_completed: number;
  total_searches_today: number;
}

export function DashboardPage() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.get<{ data: DashboardStats }>('/admin/stats'),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const statCards = [
    { name: 'Total Videos', value: stats?.data?.total_videos ?? 0, color: 'bg-blue-500' },
    { name: 'Search Chunks', value: stats?.data?.total_chunks ?? 0, color: 'bg-green-500' },
    { name: 'Processing', value: stats?.data?.videos_processing ?? 0, color: 'bg-yellow-500' },
    { name: 'Completed', value: stats?.data?.videos_completed ?? 0, color: 'bg-emerald-500' },
    { name: 'Searches Today', value: stats?.data?.total_searches_today ?? 0, color: 'bg-purple-500' },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Overview of your content library
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {statCards.map((stat) => (
          <div key={stat.name} className="card p-6">
            <div className="flex items-center gap-4">
              <div className={`h-12 w-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                <span className="text-white font-bold text-lg">{stat.value}</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                <p className="text-2xl font-semibold text-gray-900">{stat.value.toLocaleString()}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {/* Recent Videos */}
        <div className="card">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Videos</h2>
          </div>
          <div className="p-6">
            <p className="text-sm text-gray-500 text-center py-8">
              No videos yet. Add your first video to get started.
            </p>
          </div>
        </div>

        {/* Recent Searches */}
        <div className="card">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">Popular Searches</h2>
          </div>
          <div className="p-6">
            <p className="text-sm text-gray-500 text-center py-8">
              No search data available yet.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
